import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Skeleton,
  useTheme,
  Button,
  Paper,
  alpha,
  Stack,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useClients } from "../../hooks/useClients";
import { useClientsGroups, useUpdateClientStage } from "../../hooks/useClientsGroups";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useTranslation } from "react-i18next";

const EXPLICACAO_NIVEIS_CONSCIENCIA =
  "Os níveis de consciência são etapas que representam em que estágio do relacionamento ou da jornada de compra o cliente se encontra — por exemplo: ainda não conhece o problema, está considerando soluções ou já decidiu comprar. Eles ajudam a organizar o pipeline de clientes e o atendimento. Para usar esta tela, cadastre primeiro as etapas (níveis) na tela de cadastro.";

const Pipeline = () => {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { data: clientsData, isLoading, error, refetch } = useClients();
  const { data: pipelineStages, isLoading: isLoadingStages, error: errorStages } = useClientsGroups();
  const updateClientStage = useUpdateClientStage();
  const [pipeline, setPipeline] = useState({});

  useEffect(() => {
    if (!clientsData || !pipelineStages) return;

    const formattedData = pipelineStages.reduce((acc, stage) => {
      acc[String(stage.id)] = clientsData?.clients?.filter(client => client.id_grupo === stage.id) || [];
      return acc;
    }, {});

    setPipeline(formattedData ?? {}); // Evita undefined
  }, [clientsData, pipelineStages]);

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const { source, destination } = result;
      const sourceStage = source.droppableId;
      const destStage = destination.droppableId;

      if (!pipeline[sourceStage] || !pipeline[destStage]) return;

      const newPipeline = { ...pipeline };
      const [movedCard] = newPipeline[sourceStage].splice(source.index, 1);
      newPipeline[destStage].splice(destination.index, 0, movedCard);

      setPipeline(newPipeline);

      const newStage = pipelineStages?.find(stage => String(stage.id) === destStage);
      if (newStage) {
        updateClientStage.mutate(
          { clientId: movedCard.id, newStageId: newStage.id },
          {
            onError: () => {
              console.error("Erro ao atualizar estágio do cliente");
              refetch();
            }
          }
        );
      }
    },
    [pipeline, pipelineStages, updateClientStage, refetch]
  );

  if (isLoading || isLoadingStages) {
    return (
      <Box className="tour-pipeline-root" sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={28} sx={{ mb: 3 }} />
        <Divider sx={{ mb: 3, backgroundColor: theme.palette.divider }} />
        <Box sx={{ display: "flex", overflowX: "auto", gap: 2 }}>
          {[1, 2, 3, 4].map((col) => (
            <Box
              key={col}
              sx={{
                minWidth: 300,
                flex: "0 0 300px",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                p: 2,
                bgcolor: "background.paper",
              }}
            >
              <Skeleton variant="text" width="70%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
              <Divider sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error || errorStages) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Typography color="error">Erro ao carregar dados. Tente novamente.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.default
      }}>
      <Typography variant="h4" gutterBottom color={theme.palette.text.primary}>
        {t("pipelinePage.awarenessHeading")}
      </Typography>
      <Typography
        variant="body1"
        color={theme.palette.text.secondary}
        gutterBottom
      >
        {t("pipelinePage.awarenessSubtitle")}
      </Typography>
      <Divider sx={{ mb: 3, backgroundColor: theme.palette.divider }} />
      <DragDropContext onDragEnd={onDragEnd}>
        <Box className="tour-pipeline-board" sx={{ display: "flex", overflowX: "auto", gap: 2, padding: 3, width: "100%" }}>
          {pipelineStages?.length ? (
            pipelineStages.map((stage) => (
              <PipelineStage
                key={String(stage.id)}
                stage={stage}
                clients={pipeline[String(stage.id)] ?? []}
              />
            ))
          ) : (
            <Paper
              variant="outlined"
              sx={{
                width: "100%",
                maxWidth: 960,
                mx: "auto",
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.25 : 0.18)}`,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                  : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.06)} 40%, ${theme.palette.background.paper} 100%)`,
                boxShadow: theme.palette.mode === "dark"
                  ? `0 12px 40px ${alpha(theme.palette.common.black, 0.35)}`
                  : `0 12px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.12),
                      }}
                    >
                      <LightbulbOutlinedIcon sx={{ fontSize: 32 }} color="primary" />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                        Nenhum nível de consciência cadastrado
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        {EXPLICACAO_NIVEIS_CONSCIENCIA}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" icon={<PsychologyOutlinedIcon sx={{ fontSize: 18 }} />} label="Jornada do cliente" variant="outlined" sx={{ "& .MuiChip-icon": { color: "primary.main" } }} />
                    <Chip size="small" icon={<TrendingUpOutlinedIcon sx={{ fontSize: 18 }} />} label="Pipeline organizado" variant="outlined" sx={{ "& .MuiChip-icon": { color: "primary.main" } }} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/awareness-stages")}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontWeight: 600,
                      borderRadius: 2,
                      boxShadow: 2,
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    Ir para cadastro de níveis de consciência
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </DragDropContext>
    </Box>
  );
};

const PipelineStage = ({ stage, clients }) => {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const count = clients?.length ?? 0;
  const progressValue = Math.min(100, count > 0 ? (count / 10) * 100 : 0);

  return (
    <Droppable droppableId={String(stage.id)}>
      {(provided) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            flex: "0 0 300px",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === "dark" ? theme.shadows[8] : "0 8px 24px rgba(15, 23, 42, 0.08)",
            padding: 2,
            minHeight: "450px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5, pr: 0.5 }}>
            {stage.nome} ({count})
          </Typography>
          {stage.description ? (
            <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mb: 2, minHeight: 0 }}>
              {stage.description}
            </Typography>
          ) : (
            <Box sx={{ mb: 2 }} />
          )}
          <Divider sx={{ mb: 2 }} />
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 6,
              borderRadius: 5,
              mb: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              "& .MuiLinearProgress-bar": { borderRadius: 5 },
            }}
          />
          <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, mx: -0.5, px: 0.5 }}>
            {Array.isArray(clients) && clients.length > 0 ? (
              clients.map((client, index) => <PipelineCard key={String(client.id)} client={client} index={index} />)
            ) : (
              <Typography variant="body2" color={theme.palette.text.secondary} sx={{ textAlign: "center", py: 3, px: 1 }}>
                {t("pipelinePage.cardNoClientsInStage")}
              </Typography>
            )}
            {provided.placeholder}
          </Box>
        </Box>
      )}
    </Droppable>
  );
};

const PipelineCard = ({ client, index }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("settings");

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const goToDetails = () => navigate(`/clients/${client.id}`);

  const phoneRaw = client.telefone;
  const phoneStr = phoneRaw != null && String(phoneRaw).trim() !== "" ? String(phoneRaw).trim() : null;

  // Evita `0 && <Node />` — em React o número 0 é renderizado como texto solto.
  const statusRaw = client.status;
  const statusLabel =
    statusRaw != null &&
    statusRaw !== "" &&
    statusRaw !== 0 &&
    String(statusRaw).trim() !== ""
      ? String(statusRaw)
      : null;
  const showContactRow = Boolean(phoneStr) || Boolean(statusLabel);

  const registeredLabel =
    client.data_cadastro &&
    t("pipelinePage.cardRegisteredOn", {
      date: new Date(client.data_cadastro).toLocaleDateString(),
    });

  return (
    <Draggable key={String(client.id)} draggableId={String(client.id)} index={index}>
      {(provided, snapshot) => (
        <Card
          elevation={0}
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={goToDetails}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              goToDetails();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={t("pipelinePage.cardOpenDetails")}
          sx={{
            mb: 2,
            position: "relative",
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, snapshot.isDragging ? 0.9 : 1)}`,
            borderRadius: 2,
            boxShadow: snapshot.isDragging
              ? `0 12px 28px ${alpha(theme.palette.common.black, 0.18)}`
              : theme.palette.mode === "dark"
                ? `0 2px 8px ${alpha(theme.palette.common.black, 0.35)}`
                : `0 2px 10px ${alpha(theme.palette.common.black, 0.06)}`,
            cursor: "pointer",
            outline: "none",
            transition: theme.transitions.create(["box-shadow", "border-color"], {
              duration: theme.transitions.duration.shorter,
            }),
            "&:hover": {
              borderColor: alpha(theme.palette.primary.main, 0.45),
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
            "&:focus-visible": {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
            },
          }}
        >
          <Box
            sx={{
              height: 3,
              width: "100%",
              bgcolor: theme.palette.primary.main,
              opacity: snapshot.isDragging ? 1 : 0.85,
            }}
          />
          <CardContent sx={{ pt: 1.5, pb: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              <Avatar
                src={client?.imagem || undefined}
                sx={{
                  width: 44,
                  height: 44,
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  flexShrink: 0,
                }}
              >
                {!client?.imagem && getInitials(client.nome)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap title={client.nome}>
                  {client.nome ?? t("pipelinePage.cardNoName")}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap title={client.email || ""} sx={{ mt: 0.25 }}>
                  {client.email ?? t("pipelinePage.cardNoEmail")}
                </Typography>
              </Box>
              <Tooltip title={t("pipelinePage.cardDragHint")}>
                <IconButton
                  size="small"
                  {...provided.dragHandleProps}
                  aria-label={t("pipelinePage.cardDragHint")}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    mt: -0.5,
                    mr: -0.75,
                    color: "text.secondary",
                    flexShrink: 0,
                    "&:hover": { color: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  <DragIndicatorOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {showContactRow && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5, rowGap: 0.5 }}>
                {phoneStr ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                    <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {phoneStr}
                    </Typography>
                  </Box>
                ) : null}
                {statusLabel ? (
                  <Chip label={statusLabel} size="small" color="success" variant="outlined" sx={{ height: 22 }} />
                ) : null}
              </Stack>
            )}

            <Divider sx={{ my: 1.25, borderColor: alpha(theme.palette.divider, 0.85) }} />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              {registeredLabel ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: "text.secondary" }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                    {registeredLabel}
                  </Typography>
                </Box>
              ) : (
                <span />
              )}
              <ChevronRightRoundedIcon sx={{ fontSize: 22, color: "action.active", flexShrink: 0, opacity: 0.7 }} />
            </Box>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
};


export default Pipeline;
